/**
 * Paid channels — shared vocabulary for every surface that shows or filters
 * paid activity: the Paid tag in universalTable, the dashboard's Paid Traffic
 * Finder, the Set-paid-traffic modal, the paid traffic log, and the Recruitment
 * and Directory filter panels.
 *
 * Kept in one file because the awkward part — deciding which channels a row is
 * on — is a rule, not a column, and every one of those surfaces needs the same
 * answer. See resolvePaidChannels.
 *
 * Mirrors mars-backend/controllers/paidTraffic/channels.js. Adding a channel
 * means editing both.
 */

export const PAID_CHANNELS = {
  google: {
    key: 'google',
    label: 'Google Ads',
    short: 'Google',
    letter: 'G',
    where: 'Sponsored result in Google search',
    // Google's sponsored results are labelled in the same blue as its UI.
    chip: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
    solid: 'bg-blue-600 text-white'
  },
  meta: {
    key: 'meta',
    label: 'Meta (Facebook & Instagram)',
    short: 'Meta',
    letter: 'M',
    where: "Ad found in Meta's Ad Library",
    chip: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300',
    solid: 'bg-indigo-600 text-white'
  }
};

export const PAID_CHANNEL_LIST = Object.values(PAID_CHANNELS);

/** The surfaces a Meta ad can run on, as the Ad Library card lists them. */
export const META_PLATFORMS = ['Facebook', 'Instagram', 'Messenger', 'Threads', 'Audience Network'];

/** Unknown keys resolve to null so callers render nothing rather than a blank chip. */
export const channelMeta = (key) => PAID_CHANNELS[String(key || '').trim().toLowerCase()] || null;

/**
 * Which channels an affiliate is advertising on.
 *
 * affiliate_clean.paid_channels is only written when someone logs a find, but
 * SEMrush has been filling adwords_traffic for years and the merge pipeline
 * knows nothing about the column. So paid traffic with a NULL channel is
 * Google — that is exactly what SEMrush adwords traffic means — and treating it
 * as "unknown" would drop every pre-existing paid affiliate out of a Google
 * filter and strip the marker off its tag.
 *
 * The backend's paidChannelSql encodes that same NULL-means-Google rule in SQL;
 * they have to agree or a row would filter in and then render with no channel.
 *
 * undefined is different from null and returns nothing. Not every endpoint
 * selects the column yet — Untapped reads through the validation cache — and
 * those rows must keep rendering the plain Paid tag rather than assert a Google
 * marker nobody verified. Only a payload that actually carries paid_channels
 * gets to claim a channel.
 */
export const resolvePaidChannels = (paidChannels, paidTraffic) => {
  if (paidChannels === undefined) return [];

  const explicit = String(paidChannels || '')
    .split(',')
    .map((c) => c.trim().toLowerCase())
    .filter((c) => Object.prototype.hasOwnProperty.call(PAID_CHANNELS, c));

  if (explicit.length > 0) return Array.from(new Set(explicit));

  return Number(paidTraffic) > 0 ? ['google'] : [];
};

/**
 * Returns an href when a find's label is really a link, otherwise null.
 *
 * The advertiser field holds whatever identifies the ad, and in practice that
 * is often the landing page pasted straight out of the Ad Library rather than a
 * Page name like "Football Duke". Both are legitimate, so the label is rendered
 * as a link only when it actually is one — a name must never become a dead
 * anchor.
 *
 * Bare domains get https:// so "footballduke.com/jackpot-games" resolves
 * instead of being read as a relative path off the dashboard.
 */
export const labelHref = (value) => {
  const text = String(value || '').trim();
  if (!text || /\s/.test(text)) return null; // any whitespace means it is a name

  if (/^https?:\/\/\S+$/i.test(text)) return text;
  // domain.tld, optionally with a path — at least one dot, no scheme.
  if (/^[\w-]+(\.[\w-]+)+(\/\S*)?$/.test(text)) return `https://${text}`;

  return null;
};

/**
 * Deep link to one ad in Meta's Ad Library.
 *
 * This is the only reason the Library ID is worth storing: on its own it is an
 * opaque 16-digit number nobody can read, but it addresses the exact ad, so it
 * is shown as a link and never as text.
 */
export const adLibraryUrl = (id) => {
  const value = String(id || '').trim();
  return /^\d+$/.test(value) ? `https://www.facebook.com/ads/library/?id=${value}` : null;
};

/** "Google Ads", "Google Ads and Meta" — for sentences, not chips. */
export const describeChannels = (channels) => {
  const labels = (channels || []).map((c) => channelMeta(c)?.short).filter(Boolean);
  if (labels.length === 0) return '';
  if (labels.length === 1) return labels[0];
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
};
